import {Component, ElementRef, Input, OnInit, OnDestroy, ViewChild} from '@angular/core';
import {PlaylistSong} from "../types/playlist-song";
import {NgIf, NgStyle} from "@angular/common";
import {AudioService} from "../../../../audio/audio.service";
import {environment} from "../../../../../environments/environment";

@Component({
  selector: 'app-html5audio',
  standalone: true,
  imports: [
    NgIf,
    NgStyle
  ],
  templateUrl: './html5audio.component.html',
  styleUrls: ['./html5audio.component.less']
})
export class Html5audioComponent implements OnInit, OnDestroy {
  @Input() song!: PlaylistSong;
  @ViewChild('player') player!: ElementRef<HTMLAudioElement>;
  @ViewChild('timeline') timeline!: ElementRef<HTMLDivElement>;
  public percentagePlayed: number = 0;
  public duration: number = 0;
  public isDragging: boolean = false;
  private wasPlayingBeforeDrag: boolean = false;
  private globalMouseMoveListener?: (event: MouseEvent) => void;
  private globalMouseUpListener?: (event: MouseEvent) => void;
  private globalTouchMoveListener?: (event: TouchEvent) => void;
  private globalTouchEndListener?: (event: TouchEvent) => void;

  ngOnInit() {
    this.audioService.getAudioStartedEvent().subscribe(event => {
      // When another song starts, pause this one and reset its progress
      // Use setTimeout to allow the new song to start playing first
      setTimeout(() => {
        if (!this.song.isPlaying) {
          this.player.nativeElement.currentTime = 0;
        }
      }, 10);
      this.pauseSong();
    });
  }

  ngOnDestroy() {
    this.removeGlobalListeners();
  }

  public constructor(private audioService: AudioService) {
  }

  public onPlay(): void {
    this.song.isPlaying = true;
  }

  public onPause(): void {
    this.song.isPlaying = false;
  }

  public onEnded(): void {
    this.song.isPlaying = false;
    this.player.nativeElement.currentTime = 0;
  }

  public onTimeUpdate(): void {
    this.percentagePlayed = this.duration / this.player.nativeElement.currentTime;
  }

  public onLoadedMetadata(): void {
    this.duration = this.player.nativeElement.duration;
  }

  public onProgress(): void {
    // This event fires when the browser is downloading the audio
    // The buffered range will be recalculated in getBufferedPercentage()
  }

  public getDuration(): string {
    let minutes: number = Math.floor(this.duration / 60);
    let seconds: number = Math.round(this.duration - minutes * 60);

    let secondsPadded: string = seconds.toString();
    if (seconds < 10) {
      secondsPadded = `0${secondsPadded}`;
    }
    return `${minutes}:${secondsPadded}`;
  }

  public playSong(): void {
    this.audioService.sendAudioStartedEvent();
    this.player.nativeElement.play();
  }

  public pauseSong(): void {
    this.player.nativeElement.pause();
  }

  public getAudioUrl(song: PlaylistSong): string {
    return `${environment.cdn.url}/cdn/${song.song.url}`;
  }

  public getPlayedPercentage(): number {
    return this.player.nativeElement.currentTime / this.duration * 100
  }

  public getPlayedStyle(): object {
    if (this.player == undefined) {
      return {};
    }

    return {
      width: `${this.getPlayedPercentage()}%`
    }
  }

  public getBufferedPercentage(): number {
    if (!this.player || !this.duration) {
      return 0;
    }

    const buffered = this.player.nativeElement.buffered;
    if (buffered.length > 0) {
      // Get the end of the last buffered range
      const bufferedEnd = buffered.end(buffered.length - 1);
      return (bufferedEnd / this.duration) * 100;
    }
    return 0;
  }

  public getBufferedStyle(): object {
    return {
      width: `${this.getBufferedPercentage()}%`
    }
  }

  public onTimelineClick(event: MouseEvent): void {
    this.seekToPosition(event);
  }

  public onTimelineMouseDown(event: MouseEvent): void {
    this.isDragging = true;
    this.wasPlayingBeforeDrag = !this.player.nativeElement.paused;
    if (this.wasPlayingBeforeDrag) {
      this.player.nativeElement.pause();
    }
    this.seekToPosition(event);
    this.addGlobalMouseListeners();
    event.preventDefault();
  }

  public onTimelineMouseMove(event: MouseEvent): void {
    if (this.isDragging) {
      this.seekToPosition(event);
    }
  }

  public onTimelineMouseUp(): void {
    this.finishDragging();
  }

  private addGlobalMouseListeners(): void {
    this.globalMouseMoveListener = (event: MouseEvent) => {
      if (this.isDragging) {
        this.seekToPosition(event);
      }
    };
    this.globalMouseUpListener = (event: MouseEvent) => {
      this.finishDragging();
    };
    document.addEventListener('mousemove', this.globalMouseMoveListener);
    document.addEventListener('mouseup', this.globalMouseUpListener);
  }

  private removeGlobalListeners(): void {
    if (this.globalMouseMoveListener) {
      document.removeEventListener('mousemove', this.globalMouseMoveListener);
      this.globalMouseMoveListener = undefined;
    }
    if (this.globalMouseUpListener) {
      document.removeEventListener('mouseup', this.globalMouseUpListener);
      this.globalMouseUpListener = undefined;
    }
    if (this.globalTouchMoveListener) {
      document.removeEventListener('touchmove', this.globalTouchMoveListener);
      this.globalTouchMoveListener = undefined;
    }
    if (this.globalTouchEndListener) {
      document.removeEventListener('touchend', this.globalTouchEndListener);
      this.globalTouchEndListener = undefined;
    }
  }

  private finishDragging(): void {
    if (this.isDragging && this.wasPlayingBeforeDrag) {
      this.player.nativeElement.play();
    }
    this.isDragging = false;
    this.wasPlayingBeforeDrag = false;
    this.removeGlobalListeners();
  }

  public onTimelineTouchStart(event: TouchEvent): void {
    this.isDragging = true;
    this.wasPlayingBeforeDrag = !this.player.nativeElement.paused;
    if (this.wasPlayingBeforeDrag) {
      this.player.nativeElement.pause();
    }
    this.seekToPositionTouch(event);
    this.addGlobalTouchListeners();
    event.preventDefault();
  }

  public onTimelineTouchMove(event: TouchEvent): void {
    if (this.isDragging) {
      this.seekToPositionTouch(event);
    }
  }

  public onTimelineTouchEnd(): void {
    this.finishDragging();
  }

  private addGlobalTouchListeners(): void {
    this.globalTouchMoveListener = (event: TouchEvent) => {
      if (this.isDragging) {
        this.seekToPositionTouch(event);
      }
    };
    this.globalTouchEndListener = (event: TouchEvent) => {
      this.finishDragging();
    };
    document.addEventListener('touchmove', this.globalTouchMoveListener);
    document.addEventListener('touchend', this.globalTouchEndListener);
  }

  private seekToPosition(event: MouseEvent): void {
    const timelineEl = this.timeline.nativeElement;
    const rect = timelineEl.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const targetTime = percentage * this.duration;

    // Only allow seeking to buffered areas
    if (this.isTimeBuffered(targetTime)) {
      this.player.nativeElement.currentTime = targetTime;
    }
  }

  private seekToPositionTouch(event: TouchEvent): void {
    const timelineEl = this.timeline.nativeElement;
    const rect = timelineEl.getBoundingClientRect();
    const touch = event.touches[0] || event.changedTouches[0];
    const x = touch.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const targetTime = percentage * this.duration;

    // Only allow seeking to buffered areas
    if (this.isTimeBuffered(targetTime)) {
      this.player.nativeElement.currentTime = targetTime;
    }
  }

  private isTimeBuffered(time: number): boolean {
    if (!this.player || !this.duration) {
      return false;
    }

    const buffered = this.player.nativeElement.buffered;
    for (let i = 0; i < buffered.length; i++) {
      if (time >= buffered.start(i) && time <= buffered.end(i)) {
        return true;
      }
    }
    return false;
  }
}
